import { Alert, Choicebox, RadioGroup } from '@ksyos/design-system';
import type { ServiceCode } from '@common/enums/serviceCode';
import type { Uuid } from '@common/utils/uuid';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import type { IInsurerServiceDeclarationSetting } from '@frontend/components/interfaces';
import type { FundingServicePart } from '@frontend/services/api/fundingServicePartsApiService';
import { DeclarationMethod } from '@common/enums/declarationMethod';
import { formatString } from '@common/utils/formatString';
import { getValuesFromEnum } from '@common/utils/getValuesFromEnum';
import { Field } from '@frontend/components/elements/Field';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { RadioElementGroup } from '@frontend/components/fields';
import { getFundingServiceParts } from '@frontend/services/api/fundingServicePartsApiService';
import { getOneService } from '@frontend/services/api/servicesApiService';
import type { ValidationErrors } from '../../../utils/validation';
import { CareProductType } from '../../../../common/enums/careProductType';
import { ValidationErrorCode } from '../../../../common/enums/validationErrorCode';
import {
    insurerServiceDeclarationSettingAddSchema,
    insurerServiceDeclarationSettingEditSchema,
} from '../../../../common/validation/insurerServiceDeclarationSetting';
import { fetchJson } from '../../../store/fetch';
import { getSelectedLanguage } from '../../../translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import {
    CancelButton,
    DangerAlertWhenErrorCode,
    FormContainer,
    Header,
    Loading,
    SaveSubmit,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { FormContextProvider } from '../../elements/formContext';
import { DateField } from '../../fields/Date';
import { DropdownField } from '../../fields/Dropdown';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';

type InsurerServiceDeclarationSettingAddEditProps = IWithRouterProps;

interface IInsurerServiceDeclarationSettingAddEditState {
    allFundingServiceParts: FundingServicePart[];
    errorCode?: string;
    errors: ValidationErrors;
    insurerServiceDeclarationSetting: Partial<IInsurerServiceDeclarationSetting>;
    isSaving: boolean;
    service: { code: ServiceCode; id: string; useNewFundingSystem: boolean } | null;
}

class InnerInsurerServiceDeclarationSettingAddEdit extends Component<
    InsurerServiceDeclarationSettingAddEditProps,
    IInsurerServiceDeclarationSettingAddEditState
> {
    private readonly abortController = new AbortController();

    constructor(props: InsurerServiceDeclarationSettingAddEditProps) {
        super(props);

        this.state = {
            allFundingServiceParts: [],
            errors: emptyValidationErrors,
            insurerServiceDeclarationSetting: {
                careProductType: null,
                declarationMethod: undefined,
                endDate: null,
                fundingServicePartId: null,
                startDate: '',
            },
            isSaving: false,
            service: null,
        };
    }

    public async componentDidMount() {
        if (this.props.router.params.id) {
            await this.load();
        } else {
            await this.setState(({ insurerServiceDeclarationSetting }) => ({
                insurerServiceDeclarationSetting: {
                    ...insurerServiceDeclarationSetting,
                    insurerServiceDeclarationRuleId:
                        this.props.router.searchParams.get('insurerServiceDeclarationRuleId') ?? undefined,
                },
            }));
        }

        await this.loadService();
        await this.loadFundingServiceParts();
    }

    public componentWillUnmount() {
        this.abortController.abort();
    }

    public render() {
        const translate = getSelectedLanguage();

        return (
            <FixedWidthTemplate>
                <Header
                    content={
                        this.props.router.params.id
                            ? formatString(translate.common.editItem, [
                                  translate.serviceDeclarationRules.declarationSetting,
                              ])
                            : formatString(translate.common.addItem, [
                                  translate.serviceDeclarationRules.declarationSetting,
                              ])
                    }
                />
                <DangerAlertWhenErrorCode errorCode={this.state.errorCode} />
                <Loading
                    item={this.state.insurerServiceDeclarationSetting}
                    loaded={(insurerServiceDeclarationSetting) => (
                        <FormContainer onSubmit={async () => await this.save()}>
                            <FormContextProvider
                                value={{
                                    errors: this.state.errors,
                                    item: insurerServiceDeclarationSetting,
                                    onChange: async (key, value) => await this.handleChange(key, value),
                                    onDoValidate: async (key) => await this.validateOne(key),
                                    placeholderGroupName: '',
                                }}
                            >
                                <DateField
                                    birthDate
                                    fieldKey="startDate"
                                    isRequired={true}
                                    label={translate.common.startDate}
                                    placeholder={translate.common.startDate}
                                    size="medium"
                                />
                                <DateField
                                    birthDate
                                    fieldKey="endDate"
                                    label={translate.common.endDate}
                                    placeholder={translate.common.endDate}
                                    size="medium"
                                />
                                <DropdownField
                                    fieldKey="declarationMethod"
                                    isRequired={true}
                                    label={translate.insurers.declarationMethod}
                                    placeholder={formatString(translate.common.selectItem, [
                                        translate.insurers.declarationMethod,
                                    ])}
                                    selectOptions={this.getDeclarationMethodOptions()}
                                    size="large"
                                />
                                {insurerServiceDeclarationSetting.declarationMethod ===
                                    DeclarationMethod.insurerSettings && (
                                    <DropdownField
                                        fieldKey="careProductType"
                                        isRequired={true}
                                        label={translate.insurers.careProductType}
                                        placeholder={formatString(translate.common.selectItem, [
                                            translate.insurers.careProductType,
                                        ])}
                                        selectOptions={this.getCareProductTypeOptions()}
                                        size="medium"
                                    />
                                )}
                                {this.state.service?.useNewFundingSystem &&
                                    this.state.allFundingServiceParts.length > 0 &&
                                    insurerServiceDeclarationSetting.declarationMethod ===
                                        DeclarationMethod.insurerSettings && (
                                        <Field
                                            isRequired={true}
                                            label={translate.finance.fundingServicePart.shortName}
                                        >
                                            <RadioElementGroup
                                                labels={[
                                                    translate.finance.fundingServicePart.all,
                                                    translate.finance.fundingServicePart.specific,
                                                ]}
                                                name="fundingForActivityCodesEnabled"
                                                onChange={(value) => {
                                                    this.handleChange(
                                                        'fundingServicePartId',
                                                        value === 'all' ? null : this.state.allFundingServiceParts[0].id
                                                    );
                                                }}
                                                optionElements={[null, null]}
                                                options={['all', 'specific']}
                                                value={
                                                    insurerServiceDeclarationSetting.fundingServicePartId === null
                                                        ? 'all'
                                                        : 'specific'
                                                }
                                            />
                                            {insurerServiceDeclarationSetting.fundingServicePartId === null && (
                                                <Alert appearance="info">
                                                    {translate.finance.fundingServicePart.allInfo}
                                                </Alert>
                                            )}
                                            {insurerServiceDeclarationSetting.fundingServicePartId !== null && (
                                                <Alert appearance="info">
                                                    {translate.finance.fundingServicePart.specificInfo}
                                                </Alert>
                                            )}
                                        </Field>
                                    )}
                                {insurerServiceDeclarationSetting.fundingServicePartId !== null &&
                                    this.state.allFundingServiceParts.length > 0 && (
                                        <Field label={translate.finance.fundingServicePart.longName}>
                                            <RadioGroup
                                                layout="row"
                                                name="useFundingParts"
                                                onChange={(value) => {
                                                    this.handleChange(
                                                        'fundingServicePartId',
                                                        value.target.value as Uuid
                                                    );
                                                }}
                                                value={insurerServiceDeclarationSetting.fundingServicePartId}
                                            >
                                                {this.state.allFundingServiceParts.map((part) => (
                                                    <Choicebox
                                                        key={part.id}
                                                        label={part.name}
                                                        value={part.id}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        </Field>
                                    )}
                            </FormContextProvider>

                            <CancelButton
                                disabled={this.state.isSaving}
                                to={this.getReturnUrl()}
                            />
                            <SaveSubmit disabled={this.state.isSaving} />
                        </FormContainer>
                    )}
                />
            </FixedWidthTemplate>
        );
    }

    private async handleChange<K extends keyof IInsurerServiceDeclarationSetting>(
        name: K,
        value: IInsurerServiceDeclarationSetting[K]
    ) {
        await this.setState((editState: IInsurerServiceDeclarationSettingAddEditState) => {
            const { insurerServiceDeclarationSetting } = editState;
            const fundingServicePartId =
                name === 'declarationMethod' ? null : insurerServiceDeclarationSetting.fundingServicePartId;

            return {
                insurerServiceDeclarationSetting: {
                    ...insurerServiceDeclarationSetting,
                    careProductType: this.getCareProductTypeValue(insurerServiceDeclarationSetting, name, value),
                    fundingServicePartId,
                    [name]: value,
                },
            };
        });
    }

    private getCareProductTypeValue<K extends keyof IInsurerServiceDeclarationSetting>(
        insurerServiceDeclarationSetting: Partial<IInsurerServiceDeclarationSetting>,
        name: K,
        value: IInsurerServiceDeclarationSetting[K]
    ) {
        if (name === 'careProductType') {
            return value;
        }

        if (name === 'declarationMethod' && value === DeclarationMethod.insurerSettings) {
            return insurerServiceDeclarationSetting.careProductType;
        }

        if (
            name !== 'declarationMethod' &&
            insurerServiceDeclarationSetting.declarationMethod === DeclarationMethod.insurerSettings
        ) {
            return insurerServiceDeclarationSetting.careProductType;
        }

        return null;
    }

    private async validateOne(name: keyof IInsurerServiceDeclarationSetting) {
        await this.setState(({ insurerServiceDeclarationSetting, errors }) => ({
            errors: updateValidationErrorsForOne(
                insurerServiceDeclarationSettingEditSchema,
                insurerServiceDeclarationSetting,
                errors,
                name
            ),
        }));
    }

    private async load() {
        const url = `/api/insurerServiceDeclarationSettings/${this.props.router.params.id}`;
        const result = await fetchJson({ method: 'GET', signal: this.abortController.signal, url });
        await this.setState({ insurerServiceDeclarationSetting: result.json });
    }

    private async save() {
        let url = '/api/insurerServiceDeclarationSettings/';

        if (this.props.router.params.id) {
            url += this.props.router.params.id;
        }

        const { errors, item } = validate(
            this.props.router.params.id
                ? insurerServiceDeclarationSettingEditSchema
                : insurerServiceDeclarationSettingAddSchema,
            this.state.insurerServiceDeclarationSetting
        );
        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }

        await this.setState({ isSaving: true });

        const result = await fetchJson({
            allowedErrorCodes: [ValidationErrorCode.OVERLAPPING_CONFIG],
            body: item,
            method: 'POST',
            signal: this.abortController.signal,
            url,
        });

        if (result.errorCode) {
            await this.setState({ errorCode: result.errorCode, isSaving: false });
        } else {
            this.props.router.navigate(this.getReturnUrl());
        }
    }

    private getReturnUrl() {
        const searchParams = this.props.router.searchParams;
        const insurerServiceDeclarationRuleId = searchParams.get('insurerServiceDeclarationRuleId');
        const insurerId = searchParams.get('insurerId');
        const serviceId = searchParams.get('serviceId');
        const returnPage = searchParams.get('returnPage');

        const newParams = new URLSearchParams({
            ...(insurerId && { insurerId }),
            ...(serviceId && { serviceId }),
            ...(returnPage && { returnPage }),
        });

        return `/insurerServiceDeclarationRules/${insurerServiceDeclarationRuleId}/edit?${newParams.toString()}`;
    }

    private getCareProductTypeOptions() {
        return [
            { text: CareProductType.DBC, value: CareProductType.DBC },
            { text: CareProductType.MKC, value: CareProductType.MKC },
            { text: CareProductType.OVP, value: CareProductType.OVP },
        ];
    }

    private getDeclarationMethodOptions() {
        const translate = getSelectedLanguage();

        return getValuesFromEnum(DeclarationMethod)
            .map((value) => {
                return {
                    text: translate.declarationMethodEnum[value],
                    value,
                };
            })
            .sort((a, b) => a.text.localeCompare(b.text));
    }

    private async loadService() {
        let serviceId = this.props.router.searchParams.get('serviceId');
        if (!serviceId) {
            const ruleUrl = `/api/insurerServiceDeclarationRules/${this.props.router.searchParams.get('insurerServiceDeclarationRuleId')}`;
            const result = await fetchJson({ method: 'GET', url: ruleUrl });
            serviceId = result.json.serviceId;
        }
        if (!serviceId) {
            throw new Error('Service id is required');
        }
        const service = await getOneService(serviceId, this.abortController.signal);
        if (service) {
            await this.setState({ service });
        }
    }

    private async loadFundingServiceParts() {
        const serviceId = this.state.service?.id;
        if (!serviceId) {
            throw new Error('Service id is required');
        }
        const allFundingServiceParts = await getFundingServiceParts(serviceId as Uuid, this.abortController.signal);
        allFundingServiceParts.sort((a, b) => a.name.localeCompare(b.name));
        await this.setState({ allFundingServiceParts });
    }
}

export const InsurerServiceDeclarationSettingAddEdit = withRouter(InnerInsurerServiceDeclarationSettingAddEdit);
